import { fetchApi, fetchApiBlob } from '@/services/api-client';

export type MapTermRow = {
  termName: string;
  testStartDate: string | null;
  gradeLevel: number | null;
  ritScore: number | null;
  /** The score with its standard error either side, "204-207-210", as NWEA prints it. */
  ritRange: string | null;
  percentile: number | null;
  /** "63-71-77" — the band the score's own error gives it. Null when the grade is unknown. */
  percentileRange: string | null;
  normMeanRit: number | null;
  schoolMeanRit: number | null;
  schoolMeanCount: number | null;
  projectedRit: number | null;
  growth: number | null;
  growthProjection: number | null;
  metProjectedGrowth: string | null;
};

export type MapGoal = {
  name: string;
  ritScore: number | null;
  range: string | null;
  adjective: string | null;
};

export type MapSubjectBlock = {
  subject: string;
  course: string;
  terms: MapTermRow[];
  latestTermName: string | null;
  goals: MapGoal[];
  lexileRange: string | null;
  quantileRange: string | null;
  /** Inline SVG built on the server, so the page and the PDF show the same drawing. */
  chartSvg: string;
};

export type UnmatchedMapRow = {
  rawStudentId: string;
  termName: string;
  subject: string;
  course: string;
  reason: string;
};

export type MapImportSummary = {
  parsed: number;
  /** How many rows got a grade from the roster file. Zero means the comparisons stay hidden. */
  gradesFound: number;
  matched: number;
  created: number;
  updated: number;
  terms: string[];
  unmatched: UnmatchedMapRow[];
};

/** One uploaded PDF and the child it was matched to, or why it was not. */
export type MapReportMatch = {
  originalName: string;
  personId: string | null;
  studentId: string | null;
  studentName: string | null;
  reason: string | null;
};

export type MapReportSummary = {
  files: number;
  matched: MapReportMatch[];
  unmatched: MapReportMatch[];
  replacing: number;
  saved: boolean;
};

export type MapReportPending = {
  studentId: string;
  studentName: string | null;
  personId: string | null;
  recipients: number;
};

export type MapReportNotifySummary = {
  sent: MapReportPending[];
  withoutRecipients: MapReportPending[];
  emails: number;
};

function uploadReports(path: string, files: File[]): Promise<MapReportSummary> {
  const body = new FormData();
  for (const file of files) body.append('files', file);
  return fetchApi<MapReportSummary>(path, { method: 'POST', body });
}

function upload(path: string, file: File, roster?: File | null): Promise<MapImportSummary> {
  const body = new FormData();
  body.append('file', file);
  // The roster is optional: without it the scores still import, only the comparisons stay hidden.
  if (roster) body.append('roster', roster);
  return fetchApi<MapImportSummary>(path, { method: 'POST', body });
}

export const mapResultsApi = {
  forStudent: (studentId: string) => fetchApi<MapSubjectBlock[]>(`/students/${studentId}/map-results`),
  pdf: (studentId: string) => fetchApiBlob(`/students/${studentId}/map-results/pdf`),
  preview: (file: File, roster?: File | null) => upload('/map-results/preview', file, roster),
  import: (file: File, roster?: File | null) => upload('/map-results/import', file, roster),
  report: (studentId: string) => fetchApiBlob(`/students/${studentId}/map-results/report`),
  reportStatus: (studentId: string) =>
    fetchApi<{ available: boolean; originalName: string | null }>(`/students/${studentId}/map-results/report/status`),
  previewReports: (files: File[]) => uploadReports('/map-results/reports/preview', files),
  uploadReports: (files: File[]) => uploadReports('/map-results/reports', files),
  pendingReports: () => fetchApi<MapReportPending[]>('/map-results/reports/pending'),
  notifyReports: () => fetchApi<MapReportNotifySummary>('/map-results/reports/notify', { method: 'POST' }),
};
